import { AfterViewInit, Component, EventEmitter, Input, Output, ViewChild } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { AssociationSource } from "common/associations/association-browser.service";
import {
	Association,
	AssociationTarget,
	TargetAndSourceContainer,
} from "common/associations/association-list.component";
import { AssociationStore } from "common/associations/association-store.interface";
import { AssociationTargetSearchQuery, AssocSearchResult } from "common/associations/search-query.logic";
import { SearchQuery } from "common/autoCompleteSearch/search-query";
import { DialogService } from "common/dialog/dialog.service";
import { WorkspacesResource } from "core/stages-client";
import { AutoCompleteSearchComponent } from "common/autoCompleteSearch/auto-complete-search.component";
import TypedId = stages.core.TypedId;

interface SearchResult {
	current: AssocSearchResult;
	next: AssocSearchResult;
}

interface AssocSearchResultWithActions extends AssocSearchResult {
	actionsFromProcess?: Record<string, boolean>;
}

@Component({
	styleUrls: ["add-target-element.component.scss"],
	selector: "stages-add-target-element",
	templateUrl: "./add-target-element.component.html",
})
export class AddTargetElementComponent<S extends AssociationSource<T>, T extends AssociationTarget>
	implements AfterViewInit
{
	@Input()
	sourceElementTypedId!: stages.core.TypedId;

	@Input()
	sourceElement!: S;

	@Input()
	sourceRole?: string;

	@Input()
	targetElementWorkspace?: string;

	@Input()
	targetElementType!: string;

	@Input()
	targetElementSubtypes!: string[];

	@Input()
	targetElementParentId?: string;

	@Input()
	targetElementProcessIds?: string[];

	@Input()
	targetElementCreateAllowed!: boolean;

	@Input()
	commentOnlyAssociationsAllowed!: boolean;

	@Input()
	associationType!: string;

	@Input()
	targetElementsToIgnore: T[] = [];

	@Input()
	associationStore!: AssociationStore<S, T>;

	@Input()
	remoteWorkspaceSeparatorInAutocomplete = true;

	@Input()
	restrictAssociateDependentElementTypes?: string[];

	@Input()
	path!: string;

	@Input()
	sourceAssociation?: Association<T>;
	@Output() readonly addedAssociation = new EventEmitter<Association<T>>();
	@Output() readonly associationToCreate = new EventEmitter<TargetAndSourceContainer<S>>();

	@Output() readonly cancelled = new EventEmitter<void>();

	@ViewChild("autoComplete")
	autoCompleteComponent!: AutoCompleteSearchComponent<AssocSearchResult>;

	dependentElementContainer?: AssocSearchResultWithActions;

	localWorkspaceId!: string;
	localProcessVersion!: string;
	backButtonActive: boolean = true;

	constructor(
		private route: ActivatedRoute,
		private readonly dialogService: DialogService,
		private readonly translateService: TranslateService,
		private readonly workspacesResource: WorkspacesResource,
	) {}

	async ngAfterViewInit(): Promise<void> {
		if (this.sourceAssociation) {
			try {
				const targetElement = this.sourceAssociation.targetElement!.dependent
					? this.sourceAssociation.targetElement!.parent!
					: this.sourceAssociation.targetElement!;
				const results = await this.getSearchQuery().run(targetElement.label);
				const optionalTargetElement = results.filter(
					(result) => result.identity && result.identity === targetElement.identity,
				);
				if (optionalTargetElement.length === 1) {
					this.backButtonActive = false;
					this.dependentElementContainer = optionalTargetElement[0];
				}
			} catch (error: unknown) {
				this.autoCompleteComponent.passErrorToResults(error);
				this.autoCompleteComponent.searchEngineAvailable = false;
			}
		}
	}

	getSearchQuery(): SearchQuery<AssocSearchResult> {
		this.localWorkspaceId = this.route.snapshot.paramMap.get("workspaceId")!;
		this.localProcessVersion = this.route.snapshot.paramMap.get("processVersion")!;
		return new AssociationTargetSearchQuery(
			this.translateService,
			this.associationStore,
			this.targetElementType,
			this.targetElementSubtypes,
			this.targetElementsToIgnore,
			this.localWorkspaceId,
			this.getTargetElementWorkspaceId(),
			this.localProcessVersion,
			this.targetElementCreateAllowed,
			this.commentOnlyAssociationsAllowed,
			this.restrictAssociateDependentElementTypes,
			this.dependentElementContainer ?? undefined,
			this.dependentElementContainer ? this.dependentElementContainer.id : this.targetElementParentId,
			this.targetElementProcessIds,
			!!this.sourceAssociation,
		);
	}

	getInputIconClasses(): string[] {
		return this.associationStore.getInputClasses(
			this.targetElementType,
			!!this.dependentElementContainer,
			undefined,
			"ico",
		);
	}

	getIconClasses(element: AssocSearchResult, additionalClasses?: string): string[] {
		return this.associationStore.getInputClasses(
			element.type.ident,
			!!this.dependentElementContainer,
			element.type.subtypeIdent,
			additionalClasses,
		);
	}

	getIconClassesAndCheckForDependentType(element: AssocSearchResult, additionalClasses?: string): string[] {
		const iconClasses = this.associationStore.getInputClasses(
			element.type.ident,
			!!this.dependentElementContainer,
			element.type.subtypeIdent,
			additionalClasses,
		);

		if (element.isNew) {
			iconClasses.push("add");
			if (element.dependent) {
				iconClasses.push("ico-add");
			}
		} else if (!element.isNew && element.dependent) {
			iconClasses.push("ico-tag");
		}

		return iconClasses;
	}

	getLabelClasses(element: AssocSearchResult, additionalClasses?: string): string[] {
		return this.associationStore.getQuickAssignLabelClasses(element, additionalClasses);
	}

	addNewAssociationToList(association: Association<T>): void {
		this.addedAssociation.emit(association);
	}

	updateAssociationToList(assocTypeIdToAdd: TypedId, sourceElement?: S): void {
		this.associationToCreate.emit({ typedId: assocTypeIdToAdd, dependentSourceElement: sourceElement });
	}

	async selectItemCallback(element: AssocSearchResult): Promise<void> {
		if (element.isNew) {
			await this.createNewElementAndAssociation(element);
		} else if (
			element.dependentElementsContainer &&
			(this.restrictAssociateDependentElementTypes === undefined ||
				this.restrictAssociateDependentElementTypes.length > 0)
		) {
			await this.handleDependentElementsContainer(element);
		} else {
			await this.createAssociationToExistingElement(element, this.sourceElement);
		}
	}

	private async createNewElementAndAssociation(element: AssocSearchResult): Promise<void> {
		if (element.isComment) {
			await this.createCommentOnlyAssociation(element, this.localWorkspaceId);
		} else {
			await this.handleAssociationToNewElement(element);
		}
	}

	private async handleAssociationToNewElement(element: AssocSearchResult): Promise<void> {
		if (this.elementWithSameLabelExists(element)) {
			if (await this.confirmCreationOfElementWithSameName(element)) {
				await this.createAssociationToNewTargetElement(element, this.localWorkspaceId);
			}
		} else {
			await this.createAssociationToNewTargetElement(element, this.localWorkspaceId);
		}
	}

	private async handleDependentElementsContainer(element: AssocSearchResult): Promise<void> {
		this.dependentElementContainer = element;

		// We want to check if the user is able to create elements in the remote workspace
		const remoteProcessView = await this.workspacesResource.getRemoteProcessView(element.workspaceId, {
			pv: this.localProcessVersion,
		});
		this.dependentElementContainer.actionsFromProcess = remoteProcessView.actions;
	}

	private async confirmCreationOfElementWithSameName(element: AssocSearchResult): Promise<boolean> {
		return this.dialogService.confirm(
			"search.autoComplete.assocTarget.sameNameExistsCreateConfirm",
			{ name: element.label },
			"create",
			"cancel",
			false,
		);
	}

	private async createAssociationToNewTargetElement(
		element: AssocSearchResult,
		currentWorkspaceId: string,
	): Promise<void> {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Remove this comment and fix warnings! This comment was added as part of ST-32708: "Migrate from TSLint to ESLint".
		const parent: any = {};
		parent.id = element.parentId;
		parent.type = {
			ident: this.targetElementType,
		};
		parent.children = [];
		const targetSubtype =
			element.type.subtypeIdent === null && this.sourceElementTypedId.typeIdent === element.type.ident
				? this.sourceElement.type.subtypeIdent
				: element.type.subtypeIdent;
		if (this.sourceAssociation) {
			const assoc = await this.associationStore.createElementAndChangeAssociationTarget(
				parent,
				element.type.ident,
				targetSubtype!,
				element.name,
				currentWorkspaceId,
				this.localProcessVersion,
				element.dependent,
				this.sourceAssociation?.id,
				this.associationType,
				this.sourceRole,
				this.sourceElementTypedId.typeIdent,
				this.sourceElementTypedId.id,
			);
			this.addNewAssociationToList(assoc);
		} else {
			const assoc = await this.associationStore.createAssociationToNewTargetElement(
				this.sourceElementTypedId,
				parent,
				element.type.ident,
				targetSubtype,
				element.name,
				this.associationType,
				this.sourceRole,
				currentWorkspaceId,
				this.localProcessVersion,
				this.path,
				element.dependent,
			);
			this.addNewAssociationToList(assoc);
		}
	}

	private async createCommentOnlyAssociation(element: AssocSearchResult, currentWorkspaceId: string): Promise<void> {
		const assoc = await this.associationStore.createCommentOnlyAssociation(
			this.sourceElementTypedId,
			element.type.ident,
			element.type.subtypeIdent,
			this.associationType,
			this.sourceRole,
			currentWorkspaceId,
			this.localProcessVersion,
			element.name,
			this.path,
		);
		this.addNewAssociationToList(assoc);
	}

	private async createAssociationToExistingElement(element: AssocSearchResult, sourceElement?: S): Promise<void> {
		/* change existing association to existing element */
		if (this.sourceAssociation) {
			const updatedAssociation = await this.associationStore.changeAssociationSourceOrTarget(
				this.sourceElement,
				this.sourceAssociation.id,
				sourceElement!.type.ident,
				sourceElement!.id,
				element.type.ident,
				element.id!,
				this.localWorkspaceId,
				this.localProcessVersion,
				this.path,
			);
			this.addNewAssociationToList(updatedAssociation);
		} else {
			/* create association to existing element */
			const association = {
				typeIdent: element.type.ident,
				id: element.id!,
			};
			this.updateAssociationToList(association, sourceElement);
		}
	}

	private elementWithSameLabelExists(element: AssocSearchResult): boolean {
		return element.dependent
			? this.autoCompleteComponent.subordinatedResults!.filter(
					(targetElement) => targetElement.name.toLowerCase() === element.name.toLowerCase() && !targetElement.isNew,
			  ).length > 0
			: this.autoCompleteComponent.results.filter(
					(targetElement) => targetElement.name.toLowerCase() === element.name.toLowerCase() && !targetElement.isNew,
			  ).length > 0;
	}

	cancel(): void {
		this.cancelled.emit();
	}

	back(): void {
		this.dependentElementContainer = undefined;
	}

	getTargetElementWorkspaceId(): string {
		return this.targetElementWorkspace ?? this.localWorkspaceId;
	}

	showSubordinatedElementsMode(): boolean {
		return this.dependentElementContainer !== undefined;
	}

	showSeparator(element: SearchResult): boolean {
		return this.isItemInRemoteWorkspace(element) || this.isItemInLocalWorkspace(element);
	}

	isItemInLocalWorkspace(element: SearchResult): boolean {
		return element.current.isNew && !element.next.isNew && !element.next.isRemote;
	}

	isItemInRemoteWorkspace(element: SearchResult): boolean {
		return !element.current.isRemote && element.next.isRemote && this.remoteWorkspaceSeparatorInAutocomplete;
	}

	isCreateAllowed(item: unknown, selectedParentItem: unknown): boolean {
		const searchResult = item as AssocSearchResultWithActions;
		const parent = selectedParentItem as AssocSearchResultWithActions;
		return searchResult.isNew && searchResult.dependent && parent?.actionsFromProcess
			? parent.actionsFromProcess.CREATE_ELEMENTS
			: true;
	}

	hasAssignableAndNonRestrictedDependentElementSubtype(assoc: AssocSearchResultWithActions | undefined): boolean {
		return (
			AssocSearchResult.firstAssignableRestrictedDependentElementSubtype(
				assoc,
				this.restrictAssociateDependentElementTypes,
			) === undefined
		);
	}
}
